import { Component, Input, OnChanges, QueryList, ViewChild, ViewChildren, ChangeDetectionStrategy, output } from '@angular/core';
import { SmartComponentRenderingDirectiveDirective } from '../../directives/smart-component-rendering-directive.directive';
import { SmartComponent } from '../../class/smart-components/smart-component';
import { CdkDrag, CdkDragMove, CdkDragStart, Point } from '@angular/cdk/drag-drop';
import { IconHelper } from '../../class/icon-helper';
import { SmartComponentType, isSmartComponentCounterType } from '../../enum/enumSmartComponentType';
import { IonIcon } from '@ionic/angular';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-container-smart-component-grid',
    templateUrl: './container-smart-component-grid.component.html',
    styleUrls: ['./container-smart-component-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CdkDrag, SmartComponentRenderingDirectiveDirective, IonIcon, TranslatePipe]
})
export class ContainerSmartComponentGridComponent implements OnChanges {
  private _dragPositions: DragPosition[] = [];
  private _originDragPositions: DragPosition[] = [];
  private _readonlyDragPositions: DragPosition[] = [];
  private _startDragX = 0;
  private _startDragY = 0;
  private _isReCalculate = true;

  //List used to change when reordering items
  public listSmartComponentsToReorder: Array<SmartComponent> = []

  @ViewChild(SmartComponentRenderingDirectiveDirective, { static: true })
  renderingDirective: SmartComponentRenderingDirectiveDirective;
  @ViewChildren(CdkDrag) cdkDrags!: QueryList<CdkDrag>;

  @Input() listSmartComponents: Array<SmartComponent> = null;
  @Input() isReorderDisabled = true;
  readonly passReorderedList = output<Array<SmartComponent>>();

  ngOnChanges() {
    //Show correct smart components according to isReorderDisabled flag
    if (this.isReorderDisabled) {
      this.listSmartComponentsToReorder = this.listSmartComponents;
    } else {
      this.listSmartComponentsToReorder = this.listSmartComponents.filter(item => item.id !== -1);
    }
  }

  ngAfterViewChecked() {
    if (this._isReCalculate) {
      this.calculatePositions();
      this._isReCalculate = false;
    }
  }

  /**
   * Check if a widget is a counter type component for styling reorder item
   * @returns true if is a counter
   */
  public isSmartComponentCounter(kind: SmartComponentType): boolean {
    return isSmartComponentCounterType(kind);
  }

  /**
   * Get icon for smart component for elements in reorder items mode
   * @param type number that identifies smart component type
   * @returns icon path string
   */
  public getIconForSmartComponent(type: SmartComponentType): string {
    return IconHelper.getSmartComponentIconByType(type);
  }

  /**
   * The next functions are used to manage the drag and drop functionality, because
   * the Angular Drag and Drop module doesn't manage well a mixed orientation of elements
   * like a grid
   */

  /**
   * Get starting position of dragging element
   * @param event from starting dragging
   */
  public onDragStartedGetStartingPosition(event: CdkDragStart): void {
    this._isReCalculate = true;

    //Get current draggable index and get its id
    const sourceIndex = event.source.data;
    const sourceId = this._readonlyDragPositions[sourceIndex]?.id;

    //Find its position in _dragPositions array
    const dragPosition = this._dragPositions.find((position) => position.id === sourceId);

    //Give starting coordinates to it
    this._startDragX = dragPosition?.x ?? 0;
    this._startDragY = dragPosition?.y ?? 0;
  }

  /**
   * When user is dragging an item change its coordinates with current position
   * @param event 
   */
  public onDragMovedChangePosition(event: CdkDragMove): void {
    const distance = event.distance;

    //Get current draggable index and get its id
    const sourceIndex = event.source.data;
    const sourceId = this._readonlyDragPositions[sourceIndex]?.id;

    //Calculate new coordinates
    const newX = distance.x + this._startDragX;
    const newY = distance.y + this._startDragY;

    //Get initial position
    const sourcePosition = this._dragPositions.find((position) => position.id == sourceId);

    const originX = sourcePosition?.originX ?? 0;
    const originY = sourcePosition?.originY ?? 0;

    //Coordinates of element's center
    const centrePoint: Point = {
      x: originX + newX + event.source.element.nativeElement.clientWidth / 2,
      y: originY + newY + event.source.element.nativeElement.clientHeight / 2,
    };

    //Get the id of the element the user is hovering over
    const targetId = this._dragPositions
      .filter((position) => position.id !== sourceId)
      .find((position) => {
        return (
          centrePoint.x >= position.originX + position.x &&
          centrePoint.x <= position.originX + position.x + position.width &&
          centrePoint.y >= position.originY + position.y &&
          centrePoint.y <= position.originY + position.y + position.height
        );
      })?.id;

    //Update initial positions of elements when moving
    const updatedDragPositions =
      targetId === undefined
        ? [...this._originDragPositions]
        : this.swapCoordinates([...this._originDragPositions], sourceId, targetId);

    this._originDragPositions = [...updatedDragPositions];

    //Update last position of dragging element
    const index = updatedDragPositions.findIndex((position) => position.id === sourceId);
    updatedDragPositions[index] = {
      ...updatedDragPositions[index],
      x: newX,
      y: newY,
    };

    this._dragPositions = [...updatedDragPositions];
  }

  /**
   * Sets the position when dragging element is outside of a drop container
   * @param index of the element in an array
   * @returns coordinates
   */
  public getDragPosition(index: number): Point {
    const drag = this._dragPositions.find((position) => position.id === index);
    return {
      x: drag?.x ?? 0,
      y: drag?.y ?? 0,
    };
  }

  /**
   * When element ends its movement get the list with the new order
   */
  public onDragEndedChangeList(): void {
    //Get new list
    const sortedItems = this._originDragPositions.map((position) => this.listSmartComponents[position.id]);

    //Reset _dragPositions and _originDragPosition
    this._dragPositions = [...this._readonlyDragPositions];
    this._originDragPositions = [...this._readonlyDragPositions];

    //Update list with new list
    this.listSmartComponents = sortedItems;

    //Emit list to smart component page
    this.passReorderedList.emit(this.listSmartComponents);
  }

  /**
   * Calculate positions of elements
   */
  private calculatePositions(): void {
    const dragPositions: DragPosition[] = [];

    this.cdkDrags.forEach((cdkDrag, index) => {
      const dragRect = cdkDrag.element.nativeElement.getBoundingClientRect();

      const dragPosition: DragPosition = {
        x: 0,
        y: 0,
        id: index,
        width: cdkDrag.element.nativeElement.clientWidth,
        height: cdkDrag.element.nativeElement.clientHeight,
        originX: dragRect.left,
        originY: dragRect.top,
      };

      dragPositions.push(dragPosition);
    });

    this._dragPositions = dragPositions;
    this._originDragPositions = [...dragPositions];
    this._readonlyDragPositions = [...dragPositions];
  }

  /**
   * Swap coordinates between two items
   * @param data position of current drag
   * @param sourceId id of dragging item
   * @param targetId id of target item
   * @returns array with swapped coordinates
   */
  private swapCoordinates(data: DragPosition[], sourceId: number, targetId: number): DragPosition[] {
    const sourceIndex = data.findIndex((position) => position.id === sourceId);
    const targetIndex = data.findIndex((position) => position.id === targetId);

    const copiedData = [...data];

    const removedItem = copiedData.splice(sourceIndex, 1);
    copiedData.splice(targetIndex, 0, removedItem[0]);

    const updatedData = copiedData.map((dragPosition, index) => {
      const legacyDrag = this._readonlyDragPositions[index];

      if (!legacyDrag) return dragPosition;
      const x = legacyDrag.originX - dragPosition.originX;
      const y = legacyDrag.originY - dragPosition.originY;
      return {
        ...dragPosition,
        x,
        y,
      };
    });

    return updatedData;
  }
}